// Module Facturation - Interface Admin
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  FiCreditCard, FiDownload, FiTrendingUp, FiDatabase, FiUsers,
  FiCalendar, FiDollarSign, FiAlertCircle, FiCheckCircle,
  FiBarChart2, FiPackage, FiArrowUp
} from 'react-icons/fi';
import { DashboardServices } from '@/services/DashboardServices';

// Types
interface Subscription {
  plan: 'Starter' | 'Professional' | 'Enterprise';
  startDate: Date;
  renewalDate: Date;
  status: 'active' | 'trial' | 'expired';
  autoRenewal: boolean;
  monthlyPrice: number;
  yearlyPrice: number;
}

interface Usage {
  scoringsUsed: number;
  scoringsLimit: number;
  storageUsed: number;
  storageLimit: number;
  activeUsers: number;
  usersLimit: number;
}

interface ConsommationResponse {
  success: boolean;
  data: {
    total_scored: number;
    media_size_gb: number;
    users_count:number;
  };
}

interface Invoice {
  id: string;
  number: string;
  date: Date;
  amount: number;
  status: 'paid' | 'pending' | 'overdue';
  pdfUrl: string;
  period: string;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.xl};

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const PlanCard = styled.div<{ highlighted?: boolean }>`
  padding: ${theme.spacing.xl};
  background: ${props => props.highlighted ? theme.colors.primary + '10' : theme.colors.surface + '50'};
  border: 2px solid ${props => props.highlighted ? theme.colors.primary : 'transparent'};
  border-radius: ${theme.borderRadius.lg};
  position: relative;
`;

const CurrentPlanBadge = styled.div`
  position: absolute;
  top: ${theme.spacing.lg};
  right: ${theme.spacing.lg};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  background: ${theme.colors.success};
  color: white;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.bold};
  text-transform: uppercase;
`;

const PlanName = styled.div`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
`;

const PlanPrice = styled.div`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const PlanPeriod = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin-bottom: ${theme.spacing.lg};
`;

const PlanDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.lg} 0;
  border-top: 1px solid ${theme.colors.surface};
  border-bottom: 1px solid ${theme.colors.surface};
  margin-bottom: ${theme.spacing.lg};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${theme.typography.sizes.sm};
`;

const DetailLabel = styled.div`
  color: ${theme.colors.text}80;
`;

const DetailValue = styled.div`
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const RenewalInfo = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.info}10;
  border-left: 4px solid ${theme.colors.info};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  display: flex;
  align-items: start;
  gap: ${theme.spacing.sm};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};
  width: 100%;

  ${props => props.variant === 'secondary' ? `
    background: transparent;
    color: ${theme.colors.text};
    border: 1px solid ${theme.colors.surface};
    &:hover {
      background: ${theme.colors.surface};
    }
  ` : `
    background: ${theme.colors.primary};
    color: white;
    &:hover {
      background: ${theme.colors.primary}DD;
      transform: translateY(-2px);
    }
  `}
`;

const UsageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const UsageCard = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
`;

const UsageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

const UsageLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const UsageValue = styled.div<{ warning?: boolean }>`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.bold};
  color: ${props => props.warning ? theme.colors.warning : theme.colors.primary};
`;

const ProgressBar = styled.div`
  height: 10px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin-bottom: ${theme.spacing.xs};
`;

const ProgressFill = styled.div<{ percentage: number; warning?: boolean }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => {
    if (props.percentage >= 90) return theme.colors.error;
    if (props.percentage >= 75) return theme.colors.warning;
    return theme.colors.success;
  }};
  transition: width 0.5s ease;
`;

const UsageText = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const InvoicesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const InvoiceItem = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
    box-shadow: ${theme.shadows.sm};
  }
`;

const InvoiceInfo = styled.div`
  flex: 1;
`;

const InvoiceNumber = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const InvoiceMeta = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const InvoiceAmount = styled.div`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-right: ${theme.spacing.lg};
`;

const StatusBadge = styled.div<{ status: 'paid' | 'pending' | 'overdue' }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  ${props => {
    switch (props.status) {
      case 'paid':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      case 'pending':
        return `
          background: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
        `;
      case 'overdue':
        return `
          background: ${theme.colors.error}20;
          color: ${theme.colors.error};
        `;
    }
  }}
`;

const IconButton = styled.button`
  padding: ${theme.spacing.sm};
  background: ${theme.colors.primary}10;
  color: ${theme.colors.primary};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}20;
  }
`;

const AlertBox = styled.div<{ type: 'warning' | 'info' }>`
  padding: ${theme.spacing.md};
  background: ${props => props.type === 'warning' ? theme.colors.warning + '20' : theme.colors.info + '20'};
  border-left: 4px solid ${props => props.type === 'warning' ? theme.colors.warning : theme.colors.info};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: start;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  margin-top: ${theme.spacing.lg};
`;

export const BillingManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState<Usage>({
    scoringsUsed: 0,
    scoringsLimit: 200,
    storageUsed: 0,
    storageLimit: 20,
    activeUsers: 0,
    usersLimit: 5,
  });

  const subscription: Subscription = {
    plan: 'Enterprise',
    startDate: new Date('2024-01-01'),
    renewalDate: new Date('2025-01-01'),
    status: 'active',
    autoRenewal: true,
    monthlyPrice: 3500000 ,
    yearlyPrice: 17500
  };

  useEffect(() => {
    const fetchConsommation = async () => {
      try {
        setLoading(true);
        const response = await DashboardServices.consommation() as ConsommationResponse;

        if (response.success) {
          setUsage(prev => ({
            ...prev,
            scoringsUsed: response.data.total_scored,
            storageUsed: response.data.media_size_gb,
            activeUsers: response.data.users_count,
          }));
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la consommation:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConsommation();
  }, []);

  const invoices: Invoice[] = [
    {
      id: '1',
      number: 'INV-2024-001',
      date: new Date('2024-01-15'),
      amount: 999,
      status: 'paid',
      pdfUrl: '#',
      period: 'Janvier 2024'
    },
    {
      id: '2',
      number: 'INV-2023-012',
      date: new Date('2023-12-15'),
      amount: 999,
      status: 'paid',
      pdfUrl: '#',
      period: 'Décembre 2023'
    },
    {
      id: '3',
      number: 'INV-2023-011',
      date: new Date('2023-11-15'),
      amount: 999,
      status: 'paid',
      pdfUrl: '#',
      period: 'Novembre 2023'
    }
  ];

  const getStatusLabel = (status: string): string => {
    const labels: { [key: string]: string } = {
      paid: 'Payée',
      pending: 'En attente',
      overdue: 'Échue'
    };
    return labels[status] || status;
  };

  const handleUpgradePlan = () => {
    alert('Mise à niveau du plan (Fonctionnalité à implémenter)');
  };

  const handleDownloadInvoice = (invoiceId: string) => {
    alert(`Téléchargement de la facture ${invoiceId}`);
  };

  const daysUntilRenewal = Math.ceil((subscription.renewalDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <Title>Facturation & Abonnement</Title>
          <Subtitle>Gérez votre abonnement, consommation et factures</Subtitle>
        </Header>

        <TwoColumnLayout>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xl }}>
            <Section>
              <SectionTitle>
                <FiPackage /> Abonnement Actuel
              </SectionTitle>
              <PlanCard highlighted={true}>
                <CurrentPlanBadge>Plan Actuel</CurrentPlanBadge>
                <PlanName>{subscription.plan}</PlanName>
                <PlanPrice>{subscription.monthlyPrice.toLocaleString()} FCFA</PlanPrice>
                <PlanPeriod>par mois (ou {subscription.yearlyPrice} FCFA/scoring)</PlanPeriod>

                <PlanDetails>
                  <DetailRow>
                    <DetailLabel>Scorings inclus</DetailLabel>
                    <DetailValue>{usage.scoringsLimit}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Stockage</DetailLabel>
                    <DetailValue>{usage.storageLimit} GB</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Utilisateurs</DetailLabel>
                    <DetailValue>{usage.usersLimit} max</DetailValue>
                  </DetailRow>
                
                  <DetailRow>
                    <DetailLabel>Support</DetailLabel>
                    <DetailValue>Prioritaire 24/7</DetailValue>
                  </DetailRow>
                </PlanDetails>

                {/* <RenewalInfo>
                  <FiCalendar />
                  <div>
                    Renouvellement automatique le {subscription.renewalDate.toLocaleDateString('fr-FR')}
                    <div style={{ fontSize: theme.typography.sizes.xs, marginTop: '4px', opacity: 0.8 }}>
                      Dans {daysUntilRenewal} jours
                    </div>
                  </div>
                </RenewalInfo> */}

                {/* <Button style={{ marginTop: theme.spacing.lg }} onClick={handleUpgradePlan}>
                  <FiArrowUp /> Modifier le Plan
                </Button> */}
              </PlanCard>
            </Section>

            {/* <Section>
              <SectionTitle>
                <FiBarChart2 /> Historique des Factures
              </SectionTitle>
              <InvoicesList>
                {invoices.map(invoice => (
                  <InvoiceItem key={invoice.id}>
                    <InvoiceInfo>
                      <InvoiceNumber>{invoice.number}</InvoiceNumber>
                      <InvoiceMeta>
                        {invoice.date.toLocaleDateString('fr-FR')} • {invoice.period}
                      </InvoiceMeta>
                    </InvoiceInfo>
                    <InvoiceAmount>{invoice.amount} FCFA</InvoiceAmount>
                    <StatusBadge status={invoice.status}>
                      {invoice.status === 'paid' && <FiCheckCircle size={12} />}
                      {getStatusLabel(invoice.status)}
                    </StatusBadge>
                    <IconButton onClick={() => handleDownloadInvoice(invoice.id)}>
                      <FiDownload />
                    </IconButton>
                  </InvoiceItem>
                ))}
              </InvoicesList>
            </Section> */}
          </div>

          <Section>
            <SectionTitle>
              <FiTrendingUp /> Consommation
            </SectionTitle>
            {loading ? (
              <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.text + '60' }}>
                Chargement des données...
              </div>
            ) : (
              <UsageSection>
                <UsageCard>
                  <UsageHeader>
                    <UsageLabel>
                      <FiBarChart2 />
                      Scorings
                    </UsageLabel>
                    <UsageValue warning={(usage.scoringsUsed / usage.scoringsLimit) >= 0.75}>
                      {usage.scoringsUsed}/{usage.scoringsLimit}
                    </UsageValue>
                  </UsageHeader>
                  <ProgressBar>
                    <ProgressFill percentage={(usage.scoringsUsed / usage.scoringsLimit) * 100} />
                  </ProgressBar>
                  <UsageText>
                    {Math.round((usage.scoringsUsed / usage.scoringsLimit) * 100)}% utilisé
                  </UsageText>
                </UsageCard>

                <UsageCard>
                  <UsageHeader>
                    <UsageLabel>
                      <FiDatabase />
                      Stockage
                    </UsageLabel>
                    <UsageValue warning={(usage.storageUsed / usage.storageLimit) >= 0.75}>
                      {usage.storageUsed.toFixed(2)}/{usage.storageLimit} GB
                    </UsageValue>
                  </UsageHeader>
                  <ProgressBar>
                    <ProgressFill percentage={(usage.storageUsed / usage.storageLimit) * 100} />
                  </ProgressBar>
                  <UsageText>
                    {Math.round((usage.storageUsed / usage.storageLimit) * 100)}% utilisé
                  </UsageText>
                </UsageCard>

              <UsageCard>
                <UsageHeader>
                  <UsageLabel>
                    <FiUsers />
                    Utilisateurs
                  </UsageLabel>
                  <UsageValue>
                    {usage.activeUsers}/{usage.usersLimit}
                  </UsageValue>
                </UsageHeader>
                <ProgressBar>
                  <ProgressFill percentage={(usage.activeUsers / usage.usersLimit) * 100} />
                </ProgressBar>
                <UsageText>
                  {Math.round((usage.activeUsers / usage.usersLimit) * 100)}% utilisé
                </UsageText>
              </UsageCard>

             

                {(usage.storageUsed / usage.storageLimit) >= 0.75 && (
                  <AlertBox type="warning">
                    <FiAlertCircle />
                    <div>
                      <strong>Stockage proche de la limite</strong><br />
                      Envisagez de passer à un plan supérieur ou de nettoyer vos données.
                    </div>
                  </AlertBox>
                )}
              </UsageSection>
            )}
          </Section>
        </TwoColumnLayout>
      </Container>
    </DashboardLayout>
  );
};
